import { clearSession } from '../api/client.js';
import { listDevices, listSessions, logout, revokeSession } from '../api/endpoints.js';
import { clearFlags } from '../flow/machine.js';
import { useAction, useApi } from '../api/hooks.js';
import { Async, Badge, Button, Failure, Panel, Raw, Row, Screen } from '../ui/kit.js';

/**
 * Every authenticated route looks the session row up per request, so a revoke
 * bites immediately rather than when the access token happens to expire.
 */
export function DevicesScreen() {
  const sessions = useApi(listSessions, []);
  const devices = useApi(listDevices, []);
  const revoke = useAction(revokeSession, () => sessions.reload());
  // clearFlags alongside clearSession, as the header's sign-out does. This one
  // dropped only the token, so the next person to sign in on this browser
  // inherited the last one's flow flags.
  const signOut = useAction(logout, () => {
    clearFlags();
    clearSession();
  });

  return (
    <Screen
      title="Devices & sessions"
      lede="Revocation is immediate: requireAuth verifies the JWT and reads the session row on every request."
    >
      <Panel title="active sessions">
        <Async state={sessions}>
          {(data) => (
            <>
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>session</th>
                      <th>platform</th>
                      <th>app_version</th>
                      <th>created</th>
                      <th>last seen</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.sessions.map((session) => (
                      <tr key={session.session_id}>
                        <td>
                          <code>{session.session_id}</code>
                          {session.is_current && (
                            <>
                              {' '}
                              <Badge tone="ok">this device</Badge>
                            </>
                          )}
                        </td>
                        <td>{session.device_platform ?? '—'}</td>
                        <td>{session.app_version ?? '—'}</td>
                        <td>{session.created_at}</td>
                        <td>{session.last_seen_at}</td>
                        <td>
                          <Button
                            variant="danger"
                            onClick={() => revoke.run(session.session_id)}
                            disabled={revoke.pending}
                          >
                            Revoke
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.sessions.length === 0 && <p className="empty">no sessions</p>}
              {revoke.error !== null && <Failure error={revoke.error} />}
              <Raw label="GET /v1/auth/sessions" value={data} />
            </>
          )}
        </Async>
      </Panel>

      <Panel title="push devices">
        <p className="muted">
          The phone app's FCM registrations (PUT /v1/devices/token). This browser cannot register one
          — there is no web push route — so the list is read-only here; the digest and billing pushes
          go to these.
        </p>
        <Async state={devices}>
          {(data) => (
            <>
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>token_id</th>
                      <th>platform</th>
                      <th>timezone</th>
                      <th>registered</th>
                      <th>last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.devices.map((device) => (
                      <tr key={device.token_id}>
                        <td>
                          <code>{device.token_id}</code>
                        </td>
                        <td>{device.platform}</td>
                        <td>{device.timezone ?? '—'}</td>
                        <td>{device.created_at}</td>
                        <td>{device.last_seen_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.devices.length === 0 && <p className="empty">no push devices registered</p>}
              <Raw label="GET /v1/devices" value={data} />
            </>
          )}
        </Async>
      </Panel>

      <Panel title="sign out">
        <p className="muted">
          Revokes this session server-side, then drops the tokens held here. A replayed refresh token
          would revoke the whole family with <code>TOKEN_REUSED</code>.
        </p>
        <Row>
          <Button variant="secondary" onClick={() => signOut.run()} disabled={signOut.pending}>
            {signOut.pending ? 'signing out…' : 'Sign out'}
          </Button>
        </Row>
        {signOut.error !== null && <Failure error={signOut.error} />}
      </Panel>
    </Screen>
  );
}
